import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  ShieldCheck, Loader2, CheckCircle2, AlertCircle, KeyRound,
  Lock, Eye, EyeOff, User,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function OtpInput({ value, onChange, disabled }) {
  const DIGITS = 6;
  const refs = Array.from({ length: DIGITS }, () => useRef(null)); // eslint-disable-line

  const digits = value.split("").concat(Array(DIGITS).fill("")).slice(0, DIGITS);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) refs[i - 1].current?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    const next = (value.slice(0, i) + e.key + value.slice(i + 1)).slice(0, DIGITS);
    onChange(next);
    if (i < DIGITS - 1) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, DIGITS);
    if (paste) {
      onChange(paste);
      refs[Math.min(paste.length, DIGITS - 1)].current?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="admin-otp-input-group">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={() => {}}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          data-testid={`admin-otp-digit-${i}`}
          className={`w-12 h-13 text-center text-2xl font-bold rounded-xl border-2 bg-transparent
            focus:outline-none transition-colors
            ${d ? "border-orange-500 text-foreground" : "border-muted-foreground/30 text-muted-foreground"}
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          style={{ height: "52px" }}
        />
      ))}
    </div>
  );
}

export default function AdminJoinPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loadingInv, setLoadingInv] = useState(true);
  const [invError, setInvError] = useState(null);

  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/admin/superadmin-join/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "no_encontrada");
        }
        const data = await res.json();
        setInvitation(data);
        if (data.name) setName(data.name);
      } catch (e) {
        setInvError(e.message);
      } finally {
        setLoadingInv(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { toast.error("Introduce el codigo completo de 6 digitos"); return; }
    if (!name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (password.length < 6) { toast.error("La contrasena debe tener al menos 6 caracteres"); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/superadmin-join/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ otp: otp.trim(), password, name: name.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al verificar el codigo");

      setAccepted(true);
      toast.success("Cuenta de administrador creada correctamente");
    } catch (err) {
      toast.error(err.message);
      setOtp("");
    } finally {
      setSubmitting(false);
    }
  };

  const strengthScore = password.length === 0 ? 0
    : password.length < 6 ? 1
    : password.length < 10 ? 2
    : password.length < 14 ? 3
    : 4;

  if (loadingInv) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F1A]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (invError) {
    const messages = {
      expirada: "Este enlace ha expirado (48 h). Pide al administrador que reenvie la invitacion.",
      ya_usada: "Esta invitacion ya fue utilizada. Si ya tienes cuenta, inicia sesion.",
      revocada: "Esta invitacion ha sido revocada. Contacta al administrador.",
      no_encontrada: "Este enlace no es valido.",
    };
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F1A] p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-white">MenuGenius Admin</span>
          </div>
          <div className="bg-[#111827] border border-slate-800 rounded-xl p-8">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2 text-white" data-testid="admin-join-error-title">Invitacion no valida</h2>
            <p className="text-slate-400 text-sm mb-6" data-testid="admin-join-error-message">
              {messages[invError] || messages.no_encontrada}
            </p>
            <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <Link to="/admin/login">Ir al panel Admin</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F1A] p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-white">MenuGenius Admin</span>
          </div>
          <div className="bg-[#111827] border border-slate-800 rounded-xl p-8">
            <div className="w-16 h-16 rounded-full bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold mb-2 text-white" data-testid="admin-join-success-title">Cuenta creada</h2>
            <p className="text-slate-400 text-sm mb-6">
              Ya eres administrador de MenuGenius. Accede al panel de control.
            </p>
            <Button
              onClick={() => navigate("/admin")}
              data-testid="admin-join-go-to-panel"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            >
              Acceder al Panel Admin
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0B0F1A] to-[#111827] p-4">
      <div className="w-full max-w-md space-y-5">

        <div className="text-center">
          <div className="inline-flex items-center gap-2.5 mb-2">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-white">MenuGenius Admin</span>
          </div>
          <p className="text-slate-400 text-sm">Activa tu cuenta de administrador</p>
        </div>

        <div className="bg-[#111827] border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Invitacion de Administrador</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Cuenta para: <strong className="text-orange-400">{invitation?.email}</strong>
              </p>
              {invitation?.invited_by_email && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Invitado por: {invitation.invited_by_email}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-[#111827] border border-slate-800 rounded-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-join-form">

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-300">Codigo de acceso *</Label>
              <OtpInput value={otp} onChange={setOtp} disabled={submitting} />
              <p className="text-xs text-center text-muted-foreground">
                Revisa tu correo en <strong className="text-slate-400">{invitation?.email}</strong>
              </p>
            </div>

            <div>
              <Label htmlFor="admin-join-name" className="text-sm font-medium text-slate-300">Tu nombre completo *</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="admin-join-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre completo"
                  required
                  className="pl-9 bg-[#0B0F1A] border-slate-700 text-white placeholder:text-slate-600"
                  data-testid="admin-join-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="admin-join-pass" className="text-sm font-medium text-slate-300">Elige una contrasena *</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="admin-join-pass"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimo 6 caracteres"
                  className="pl-9 pr-10 bg-[#0B0F1A] border-slate-700 text-white placeholder:text-slate-600"
                  data-testid="admin-join-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-slate-300"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <div className="mt-1.5 flex gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i <= strengthScore
                          ? strengthScore <= 1 ? "bg-red-400"
                            : strengthScore <= 2 ? "bg-amber-400"
                            : "bg-emerald-500"
                          : "bg-slate-800"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              disabled={submitting || otp.length < 6 || !name.trim() || password.length < 6}
              data-testid="admin-join-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando cuenta...</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" />Activar cuenta Admin</>
              )}
            </Button>

          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          ¿Ya tienes acceso?{" "}
          <Link to="/admin/login" className="text-orange-400 underline">Iniciar sesion</Link>
        </p>
      </div>
    </div>
  );
}
